import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "drawer", "locationInput", "radiusSelector"]
  static values = {
    mapboxToken: String,
    language: { type: String, default: "el" }
  }

  connect() {
    // Prevent body scroll when drawer is open
    this.boundHandleEscape = this.handleEscape.bind(this)
    
    // Show radius selector if coordinates are in the hidden form fields
    const form = this.element.querySelector('form')
    if (form && this.hasLocationInputTarget) {
      const latField = form.querySelector('input[name="latitude"]')
      const lonField = form.querySelector('input[name="longitude"]')
      if (latField && lonField && latField.value && lonField.value) {
        this.showRadiusSelector()
      }
    }
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleEscape)
  }

  open(event) {
    event?.preventDefault()
    
    // Show overlay
    this.overlayTarget.classList.remove('hidden')
    
    // Trigger reflow for animation
    this.overlayTarget.offsetHeight
    
    // Slide up drawer
    this.overlayTarget.classList.add('opacity-100')
    this.drawerTarget.classList.remove('translate-y-full')
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden'
    
    // Listen for escape key
    document.addEventListener('keydown', this.boundHandleEscape)
  }

  close(event) {
    event?.preventDefault()
    
    // Slide down drawer
    this.drawerTarget.classList.add('translate-y-full')
    this.overlayTarget.classList.remove('opacity-100')
    
    // Hide overlay after animation
    setTimeout(() => {
      this.overlayTarget.classList.add('hidden')
      document.body.style.overflow = ''
    }, 300)
    
    // Remove escape listener
    document.removeEventListener('keydown', this.boundHandleEscape)
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  getNearby(event) {
    event.preventDefault()
    
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser')
      return
    }

    // Show loading state
    const button = event.currentTarget
    const originalHTML = button.innerHTML
    button.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>Getting location...'
    button.disabled = true

    navigator.geolocation.getCurrentPosition(
      // Success
      async (position) => {
        const lat = position.coords.latitude
        const lon = position.coords.longitude
        
        // Reverse geocode to get place name
        await this.reverseGeocode(lat, lon)
        
        // Show radius selector - let user choose before submitting
        this.showRadiusSelector()
        
        // Restore button
        button.innerHTML = originalHTML
        button.disabled = false
      },
      // Error
      (error) => {
        console.error('Geolocation error:', error)
        alert('Could not get your location. Please enter it manually or enable location services.')
        
        // Restore button
        button.innerHTML = originalHTML
        button.disabled = false
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    )
  }

  async reverseGeocode(lat, lon) {
    try {
      if (!this.mapboxTokenValue) {
        console.error('Mapbox token not found, falling back to coordinates')
        this.locationInputTarget.value = `${lat},${lon}`
        return
      }
      
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?` +
        `access_token=${this.mapboxTokenValue}&` +
        `language=${this.languageValue}&` +
        `limit=1`
      
      const response = await fetch(url)
      const data = await response.json()
      
      if (data.features && data.features.length > 0) {
        const place = data.features[0]
        // Set the location input to the place name (human-readable)
        this.locationInputTarget.value = place.place_name
        
        // Store actual coordinates in the form's hidden fields for search
        const form = this.locationInputTarget.closest('form')
        if (form) {
          const latField = form.querySelector('input[name="latitude"]')
          const lonField = form.querySelector('input[name="longitude"]')
          if (latField) latField.value = lat
          if (lonField) lonField.value = lon
        }
      } else {
        // Fallback to coordinates
        this.locationInputTarget.value = `${lat},${lon}`
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error)
      // Fallback to coordinates
      this.locationInputTarget.value = `${lat},${lon}`
    }
  }

  showRadiusSelector() {
    if (this.hasRadiusSelectorTarget) {
      this.radiusSelectorTarget.style.display = 'block'
    }
  }
}
